import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import "./BuffetPricing.css";

const BuffetPricing = () => {
  const [ages, setAges] = useState({ lunch: "", dinner: "", weekend: "" });
  const [prices, setPrices] = useState({
    lunch: null,
    dinner: null,
    weekend: null,
  });

  const pricingRates = {
    lunch: 1.25,
    dinner: 1.75,
    weekend: 1.9,
    holiday: 1.95,
  };

  const handleCalculatePrice = (type) => {
    const age = parseInt(ages[type], 10);
    if (isNaN(age) || age < 0) {
      alert("Please enter a valid age.");
      return;
    }
    if (age <= 2) {
      setPrices((prev) => ({ ...prev, [type]: "Free!" }));
    } else if (age >= 11) {
      setPrices((prev) => ({ ...prev, [type]: "They’re adult price" }));
    } else {
      setPrices((prev) => ({
        ...prev,
        [type]: `$${(age * pricingRates[type]).toFixed(2)}`,
      }));
    }
  };

  return (
    <div className="buffet-background">
      <Helmet>
        <title>Buffet Pricing | Hiro Japanese Buffet</title>
        <meta
          name="description"
          content="Lunch, dinner, and weekend buffet pricing for adults, kids, and seniors at Hiro Japanese Buffet. Use our age calculator to find your child's cost."
        />
        <link
          rel="canonical"
          href="https://hirojapanesebuffet.com/buffet-pricing"
        />
        <meta
          property="og:title"
          content="Buffet Pricing | Hiro Japanese Buffet"
        />
        <meta
          property="og:description"
          content="Affordable buffet pricing for sushi, hibachi, seafood, and take-out at Hiro Japanese Buffet in Aurora, CO."
        />
        <meta
          property="og:url"
          content="https://hirojapanesebuffet.com/buffet-pricing"
        />
        <meta
          property="og:image"
          content="https://hirojapanesebuffet.com/images/collage1.jpg"
        />
      </Helmet>
      <div className="floating-intro">
        <p className="floating-eyebrow">HIRO JAPANESE BUFFET</p>

        <h1>Buffet Pricing</h1>

        <p>All-you-can-eat sushi, hibachi, seafood & fresh favorites.</p>
      </div>
      <Container className="buffet-container py-5">
        {/* LUNCH */}
        <section className="pricing-section-modern mb-5">
          <Row className="align-items-center">
            <Col lg={8}>
              <div className="pricing-content">
                <h2>Lunch Buffet</h2>

                <p className="pricing-time">
                  Monday - Friday • 11:00 AM - 3:00 PM
                </p>

                <div className="price-line">
                  <span>Adult</span>
                  <span>$14.99</span>
                </div>

                <div className="price-line">
                  <span>Children 2 & Under</span>
                  <span>Free</span>
                </div>

                <div className="price-line">
                  <span>Kids (3-10)</span>
                  <span>$1.25 per year</span>
                </div>

                <div className="price-line">
                  <span>Seniors (65+)</span>
                  <span>10% Off</span>
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="modern-calculator">
                <h5>Kid Price Calculator</h5>

                <Form.Control
                  type="number"
                  placeholder="Enter age"
                  value={ages.lunch}
                  onChange={(e) => setAges({ ...ages, lunch: e.target.value })}
                />

                <Button
                  className="mt-3"
                  onClick={() => handleCalculatePrice("lunch")}
                >
                  Calculate
                </Button>

                {prices.lunch && (
                  <div className="modern-price-result mt-3">{prices.lunch}</div>
                )}
              </div>
            </Col>
          </Row>
        </section>

        {/* DINNER */}
        <section className="pricing-section-modern mb-5">
          <Row className="align-items-center">
            <Col lg={8}>
              <div className="pricing-content">
                <h2>Dinner Buffet</h2>

                <p className="pricing-time">
                  Monday - Thursday • 4:30 PM - 9:30 PM
                </p>

                <div className="price-line">
                  <span>Adult</span>
                  <span>$21.99</span>
                </div>

                <div className="price-line">
                  <span>Children 2 & Under</span>
                  <span>Free</span>
                </div>

                <div className="price-line">
                  <span>Kids (3-10)</span>
                  <span>$1.75 per year</span>
                </div>

                <div className="price-line">
                  <span>Seniors (65+)</span>
                  <span>10% Off</span>
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="modern-calculator">
                <h5>Kid Price Calculator</h5>

                <Form.Control
                  type="number"
                  placeholder="Enter age"
                  value={ages.dinner}
                  onChange={(e) => setAges({ ...ages, dinner: e.target.value })}
                />

                <Button
                  className="mt-3"
                  onClick={() => handleCalculatePrice("dinner")}
                >
                  Calculate
                </Button>

                {prices.dinner && (
                  <div className="modern-price-result mt-3">
                    {prices.dinner}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </section>

        {/* WEEKEND */}
        <section className="pricing-section-modern mb-5">
          <Row className="align-items-center">
            <Col lg={8}>
              <div className="pricing-content">
                <h2>Weekend & Holidays</h2>

                <p className="pricing-time">
                  Friday Dinner • Saturday & Sunday
                </p>

                <div className="price-line">
                  <span>Adult</span>
                  <span>$23.99</span>
                </div>

                <div className="price-line">
                  <span>Kids Weekend</span>
                  <span>$1.90 per year</span>
                </div>

                <div className="price-line">
                  <span>Kids Holiday</span>
                  <span>$1.95 per year</span>
                </div>

                <div className="price-line">
                  <span>Seniors (65+)</span>
                  <span>10% Off</span>
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="modern-calculator">
                <h5>Kid Price Calculator</h5>

                <Form.Control
                  type="number"
                  placeholder="Enter age"
                  value={ages.weekend}
                  onChange={(e) =>
                    setAges({ ...ages, weekend: e.target.value })
                  }
                />

                <Button
                  className="mt-3"
                  onClick={() => handleCalculatePrice("weekend")}
                >
                  Calculate
                </Button>

                {prices.weekend && (
                  <div className="modern-price-result mt-3">
                    {prices.weekend}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </section>

        {/* TAKEOUT */}
        <section className="pricing-section-modern takeout-modern">
          <div className="pricing-content">
            <h2>Takeout Buffet</h2>

            <p className="pricing-time">
              Prefer takeout? Build your own buffet plate to-go.
            </p>

            <div className="price-line">
              <span>Regular</span>
              <span>$6.99/lb</span>
            </div>

            <div className="price-line">
              <span>Mixed</span>
              <span>$8.99/lb</span>
            </div>

            <div className="price-line">
              <span>Seafood</span>
              <span>$10.99/lb</span>
            </div>

            <div className="price-line">
              <span>Sushi</span>
              <span>75¢ each</span>
            </div>

            <div className="price-line">
              <span>Specialty Sushi</span>
              <span>$1.25 each</span>
            </div>

            <div className="takeout-note">
              Pricing may vary depending on item selection.
            </div>
          </div>
        </section>
      </Container>
    </div>
  );
};

export default BuffetPricing;
